import React, { useEffect, useState } from "react";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { PageHeader, TableShell, Drawer, Field } from "@/components/ui-kit";
import { toast } from "sonner";
import { Copy, KeyRound } from "lucide-react";

export default function ApiKeys() {
  const [keys, setKeys] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ company_id: "", label: "" });
  const [newlyCreated, setNewlyCreated] = useState(null);

  const load = () => api.get("/api-keys").then((r) => setKeys(r.data));
  useEffect(() => {
    load();
    api.get("/companies").then((r) => setCompanies(r.data));
  }, []);

  const create = async () => {
    try {
      const { data } = await api.post("/api-keys", form);
      setNewlyCreated(data);
      setOpen(false);
      setForm({ company_id: "", label: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const revoke = async (id) => {
    if (!confirm("Revoke this key? Ongoing integrations will stop working.")) return;
    await api.delete(`/api-keys/${id}`);
    toast.success("Revoked");
    load();
  };

  const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Copied"); };

  const companyName = (id) => companies.find((c) => c.id === id)?.name || "—";
  const companyOpts = [{ value: "", label: "— Select company —" }, ...companies.map((c) => ({ value: c.id, label: c.name }))];

  return (
    <div data-testid="apikeys-page">
      <PageHeader
        subtitle="Integrations"
        title="API Keys"
        action={<button onClick={() => setOpen(true)} className="btn-primary" data-testid="create-key-btn">+ New Key</button>}
      />

      {newlyCreated && (
        <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-md p-4">
          <div className="text-sm font-semibold text-emerald-800">Key created. Copy it now. Only a hash is stored, so it cannot be shown again.</div>
          <div className="mt-2 flex items-center gap-2 font-mono text-sm bg-white border border-emerald-300 rounded-md px-3 py-2">
            <KeyRound size={14} className="text-emerald-700" />
            <div className="flex-1 truncate" data-testid="new-key-value">{newlyCreated.key}</div>
            <button onClick={() => copy(newlyCreated.key)} className="text-xs text-emerald-700 hover:underline flex items-center gap-1">
              <Copy size={12} /> Copy
            </button>
          </div>
          <button onClick={() => setNewlyCreated(null)} className="text-xs text-emerald-700 hover:underline mt-2">Dismiss</button>
        </div>
      )}

      <TableShell columns={["Label", "Company", "Key", "Created", "Actions"]} empty={keys.length === 0 ? "No API keys yet. Create one to let a client push rides." : null}>
        {keys.map((k) => (
          <tr key={k.id} className="tbl-row border-t border-slate-100">
            <td className="px-4 py-2.5 font-medium">{k.label}</td>
            <td className="px-4 py-2.5 text-slate-600">{companyName(k.company_id)}</td>
            <td className="px-4 py-2.5 font-mono text-xs text-slate-700" title="Only the hash is stored. The full key was shown once at creation.">
              {k.key_prefix || "nemt_"}••••{k.key_last4 || ""}
            </td>
            <td className="px-4 py-2.5 font-mono text-xs">{k.created_at?.slice(0, 10)}</td>
            <td className="px-4 py-2.5">
              <button onClick={() => revoke(k.id)} className="text-rose-600 hover:underline text-xs" data-testid={`revoke-${k.id}`}>Revoke</button>
            </td>
          </tr>
        ))}
      </TableShell>

      <section className="mt-8 bg-white border border-slate-200 rounded-md p-6">
        <div className="font-display font-semibold text-slate-900 mb-2">Ingestion endpoint</div>
        <p className="text-sm text-slate-600 mb-3">POST rides to this endpoint using the <code className="font-mono text-[13px] bg-slate-100 px-1 py-0.5 rounded">X-API-Key</code> header.</p>
        <pre className="bg-slate-900 text-slate-100 text-xs font-mono rounded-md p-4 overflow-x-auto">{`curl -X POST '${API_BASE}/ingest/rides' \\
  -H 'X-API-Key: <YOUR_KEY>' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "patient_name": "John Doe",
    "pickup_address": "123 Main St",
    "dropoff_address": "Riverdale Clinic",
    "pickup_datetime": "2026-03-01T09:30",
    "mobility": "wheelchair",
    "trip_type": "one_way",
    "payer": "Medicaid"
  }'`}</pre>
      </section>

      <Drawer open={open} onClose={() => setOpen(false)} title="New API Key" footer={<>
        <button onClick={() => setOpen(false)} className="text-sm px-3 py-2 rounded-md border border-slate-200">Cancel</button>
        <button onClick={create} className="btn-primary" data-testid="save-key-btn">Create</button>
      </>}>
        <div className="space-y-4">
          <Field label="Client company" testId="k-company" value={form.company_id} onChange={(e) => setForm({ ...form, company_id: e.target.value })} options={companyOpts} />
          <Field label="Label" testId="k-label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="e.g. Production" />
        </div>
      </Drawer>
    </div>
  );
}
